import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tiReportService from '../../services/TIReportService.js';
import tankService from '../../services/TankService.js';
import userService from '../../services/UserService.js';
import locationService from '../../services/LocationService.js';
import { FileText, ArrowLeft, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import usePermissions from '../../hooks/usePermissions';
import { shouldLockInspectorInEdit } from '../../utils/roleBasedUIHelpers.js';

const EditTIReport = () => {
  const { tiReportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isSystemAdmin,
    isAdmin,
    isDocumentationManager,
    isSurveyInspector,
    isAccountant,
  } = usePermissions();
  const canEditPublishFields = isSystemAdmin || isAdmin || isDocumentationManager;

  const [formData, setFormData] = useState({
    client_id: '',
    tank_id: '',
    location_id: '',
    date_of_survey: '',

    // Tank snapshot fields
    tank_no: '',
    tank_type: '',
    maximum_gross_weight: '',
    tare_weight: '',
    capacity_ltr: '',
    csc: '',
    depot_name: '',
    led: '',
    ned: '',
    manufacture_date: '',

    // TI Report specific
    external_remarks: '',
    internal_remarks: '',
    ancillary_fittings: '',
    remarks: '',

    // Workflow
    send_to_dm: false,
    published: false,

    // Inspector
    inspected_by_id: '',
  });

  const [clients, setClients] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [useExistingTank, setUseExistingTank] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchInspectors();
    fetchLocations();
    fetchTIReport();
  }, [tiReportId]);

  useEffect(() => {
    if (useExistingTank && formData.client_id) {
      fetchClientTanks(formData.client_id);
    } else if (!useExistingTank) {
      setTanks([]);
    }
  }, [formData.client_id, useExistingTank]);

  useEffect(() => {
    if (useExistingTank && formData.tank_id) {
      loadTankData(formData.tank_id);
    }
  }, [formData.tank_id, useExistingTank]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchClientTanks = async (clientId) => {
    try {
      const response = await tankService.getByClientId(clientId);
      setTanks(response.data || []);
    } catch (err) {
      Alert.error('Failed to load tanks');
      console.error(err);
    }
  };

  const fetchInspectors = async () => {
    try {
      const response = await userService.getByRole(4);
      setInspectors(response.data || []);
    } catch (err) {
      console.error('Failed to load inspectors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  const loadTankData = async (tankId) => {
    try {
      const response = await tankService.getById(tankId);
      const tank = response.data;
      setFormData(prev => ({
        ...prev,
        tank_no: tank.tank_no || '',
        tank_type: tank.tank_type || '',
        maximum_gross_weight: tank.maximum_gross_weight || '',
        tare_weight: tank.tare_weight || '',
        capacity_ltr: tank.capacity_ltr || '',
        csc: tank.csc || '',
        depot_name: tank.depot_name || '',
        manufacture_date: tank.manufacture_date || '',
        led: tank.led || '',
        ned: tank.ned || '',
      }));
    } catch (err) {
      Alert.error('Failed to load tank data');
      console.error(err);
    }
  };

  const handleUseExistingTankToggle = (e) => {
    const checked = e.target.checked;
    setUseExistingTank(checked);

    if (checked) {
      if (formData.client_id) {
        fetchClientTanks(formData.client_id);
      }
    } else {
      setTanks([]);
      setFormData(prev => ({
        ...prev,
        tank_id: '',
        tank_no: '',
        tank_type: '',
        maximum_gross_weight: '',
        tare_weight: '',
        capacity_ltr: '',
        csc: '',
        depot_name: '',
        manufacture_date: '',
        led: '',
        ned: ''
      }));
    }
  };

  const fetchTIReport = async () => {
    try {
      const response = await tiReportService.getById(tiReportId);
      const report = response.data;

      setFormData({
        client_id: report.client_id || '',
        tank_id: report.tank_id || '',
        location_id: report.location_id || '',
        date_of_survey: report.date_of_survey || '',

        // Tank snapshot
        tank_no: report.tank_no || '',
        tank_type: report.tank_type || '',
        maximum_gross_weight: report.maximum_gross_weight || '',
        tare_weight: report.tare_weight || '',
        capacity_ltr: report.capacity_ltr || '',
        csc: report.csc || '',
        depot_name: report.depot_name || '',
        led: report.led || '',
        ned: report.ned || '',
        manufacture_date: report.manufacture_date || '',

        // TI specific
        external_remarks: report.external_remarks || '',
        internal_remarks: report.internal_remarks || '',
        ancillary_fittings: report.ancillary_fittings || '',
        remarks: report.remarks || '',

        // Workflow
        send_to_dm: report.send_to_dm || false,
        published: report.published || false,

        // Inspector
        inspected_by_id: report.inspected_by_id || '',
      });

      // If report has a tank_id, enable the existing tank toggle
      if (report.tank_id) {
        setUseExistingTank(true);
        if (report.client_id) {
          fetchClientTanks(report.client_id);
        }
      }
    } catch (err) {
      Alert.error('Failed to load TI report data');
      console.error(err);
      navigate('/ti-reports');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberedTextChange = (e) => {
    const { name, value } = e.target;
    
    // If user is typing and field is empty or just whitespace, add "1. "
    if (value.trim() !== '' && !value.match(/^\d+\./)) {
      setFormData(prev => ({
        ...prev,
        [name]: `1. ${value}`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleNumberedKeyDown = (e) => {
    const { name, value, selectionStart } = e.target;
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Count only lines that start with numbers (main points)
      const lines = value.split('\n');
      let numberedLineCount = 0;
      
      for (let line of lines) {
        if (line.trim().match(/^\d+\./)) {
          numberedLineCount++;
        }
      }
      
      // Insert new line with next number
      const nextNumber = numberedLineCount + 1;
      const beforeCursor = value.substring(0, selectionStart);
      const afterCursor = value.substring(selectionStart);
      
      const newValue = `${beforeCursor}\n${nextNumber}. ${afterCursor}`;
      
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
      
      // Set cursor position after the number
      setTimeout(() => {
        const newPosition = selectionStart + `\n${nextNumber}. `.length;
        e.target.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanedData = { ...formData };

      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null;
        }
      });

      await tiReportService.update(tiReportId, cleanedData);
      Alert.success('TI Report updated successfully!');
      setTimeout(() => navigate('/ti-reports'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update TI report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading TI report data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">

                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <FileText className="w-5 h-5 text-buttonBg" /> Edit TI Report
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/ti-reports')}
                    className="
                      relative overflow-hidden
                      group flex items-center justify-center gap-2
                      px-3 sm:px-3 py-2.5 sm:py-2
                      text-xs sm:text-sm
                      rounded-lg
                      border border-buttonBg
                      bg-buttonBg text-buttonTxt
                      font-medium whitespace-nowrap
                      transition-colors duration-300 ease-out
                      hover:bg-transparent hover:text-buttonBg
                    "
                  >
                    <span
                      className="
                        absolute inset-0
                        bg-buttonBg
                        scale-x-100
                        group-hover:scale-x-0
                        origin-center
                        transition-transform duration-300 ease-out
                        z-0
                      "
                    />
                    <ArrowLeft
                      className="
                        w-4 h-4 relative z-10
                        transition-transform duration-300 ease-out
                        group-hover:scale-90
                      "
                    />
                    <span className="relative z-10">Back to TI Reports</span>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">

                      {/* Client */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Client <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                          disabled
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.name} - {client.organization_name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Client cannot be changed</p>
                      </div>

                      {/* Tank */}
                      <div>
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <input
                            type="checkbox"
                            id="use_existing_tank"
                            checked={useExistingTank}
                            onChange={handleUseExistingTankToggle}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                            disabled={!formData.client_id}
                          />
                          <label htmlFor="use_existing_tank" className="text-xs sm:text-sm font-medium text-secondary">
                            Use Existing Tank
                          </label>
                        </div>
                        {useExistingTank && (
                          <select
                            name="tank_id"
                            value={formData.tank_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required={useExistingTank}
                            disabled={!formData.client_id}
                          >
                            <option value="">Select Tank</option>
                            {tanks.map((tank) => (
                              <option key={tank.id} value={tank.id}>
                                {tank.tank_no || `Tank ${tank.id}`}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Location */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="location_id"
                          value={formData.location_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Location</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.city}, {loc.state}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Date of Survey */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Survey <span className="text-danger font-bold">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="date"
                            name="date_of_survey"
                            value={formData.date_of_survey || ''}
                            onChange={handleChange}
                            required
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none" />
                        </div>
                      </div>

                      {/* Inspector */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Inspected By <span className="text-danger font-bold">*</span>
                          {isSurveyInspector && <span className="text-blue-600 ml-2">(Locked)</span>}
                        </label>
                        <select
                          name="inspected_by_id"
                          value={formData.inspected_by_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base ${isSurveyInspector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          required
                          disabled={isSurveyInspector}
                        >
                          <option value="">Select Inspector</option>
                          {inspectors.map((inspector) => (
                            <option key={inspector.user_id} value={inspector.user_id}>
                              {inspector.name}{isSurveyInspector && inspector.user_id === user?.user_id ? ' (You)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {(() => {
                        const showPublishCheckbox = canEditPublishFields;
                        const showSendToDMCheckbox = true;
                        const sendToDMDisabled = false;
                        const publishDisabled = !canEditPublishFields;
                        return (
                          <>
                            {showSendToDMCheckbox && (
                              <div className="flex items-center gap-2 mt-6">
                                <input
                                  type="checkbox"
                                  id="send_to_dm"
                                  name="send_to_dm"
                                  checked={formData.send_to_dm}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={sendToDMDisabled}
                                />
                                <label htmlFor="send_to_dm" className="text-xs sm:text-sm font-medium text-secondary">
                                  Send to DM
                                </label>
                              </div>
                            )}
                            {showPublishCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="published"
                                  name="published"
                                  checked={formData.published}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={publishDisabled}
                                />
                                <label htmlFor="published" className="text-xs sm:text-sm font-medium text-secondary">
                                  Publish to Client
                                </label>
                              </div>
                            )}
                          </>
                        );
                      })()}

                    </div>
                  </div>

                  {/* Section 2: Tank Snapshot */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Tank Details (From Selected Tank)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank No</label>
                        <input
                          type="text"
                          name="tank_no"
                          value={formData.tank_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank Type</label>
                        <input
                          type="text"
                          name="tank_type"
                          value={formData.tank_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">MGW (kg)</label>
                        <input
                          type="text"
                          name="maximum_gross_weight"
                          value={formData.maximum_gross_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tare Weight (kg)</label>
                        <input
                          type="text"
                          name="tare_weight"
                          value={formData.tare_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Capacity (Ltr)</label>
                        <input
                          type="text"
                          name="capacity_ltr"
                          value={formData.capacity_ltr}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CSC</label>
                        <input
                          type="text"
                          name="csc"
                          value={formData.csc}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Depot Name</label>
                        <input
                          type="text"
                          name="depot_name"
                          value={formData.depot_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manufacture Date</label>
                        <div className="relative">
                          <input
                            type="date"
                            name="manufacture_date"
                            value={formData.manufacture_date || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">LED</label>
                        <div className="relative">
                          <input
                            type="date"
                            name="led"
                            value={formData.led || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">NED</label>
                        <div className="relative">
                          <input
                            type="date"
                            name="ned"
                            value={formData.ned || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none" />
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Section 3: Inspection Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Inspection Remarks
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">External Remarks</label>
                        <textarea
                          name="external_remarks"
                          value={formData.external_remarks}
                          onChange={handleNumberedTextChange}
                          onKeyDown={handleNumberedKeyDown}
                          rows="6"
                          placeholder="Press Enter for new numbered point. Press Shift+Enter for new line within same point."
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          style={{ whiteSpace: 'pre-wrap' }}
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Internal Remarks</label>
                        <textarea
                          name="internal_remarks"
                          value={formData.internal_remarks}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Enter internal remarks"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Ancillary Fittings</label>
                        <textarea
                          name="ancillary_fittings"
                          value={formData.ancillary_fittings}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Enter ancillary fittings details"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remarks</label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          rows="3"
                          placeholder="Enter general remarks"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-3 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-buttonBg text-buttonTxt
                        font-medium whitespace-nowrap
                        transition-colors duration-300 ease-out
                        hover:bg-transparent hover:text-buttonBg
                      "
                    >
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-100
                          group-hover:scale-x-0
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
                      <span className="relative z-10">{submitting ? 'Updating...' : 'Update TI Report'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate('/ti-reports')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap
                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>

                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTIReport;
